/**
 * AuthContext — Supabase Auth 세션 관리
 *
 * onAuthStateChange로 로그인/로그아웃 이벤트를 실시간 감지하고,
 * 전체 앱에 사용자 상태를 전파한다.
 *
 * 세션 로드 후 /api/v2/auth/me를 호출해 profile(role, tenantId 등)을 가져온다.
 *
 * [버그 수정] 서버 재시작 후 profile이 null로 초기화되는 문제:
 * - fetchProfile 실패 시 재시도 로직 (네트워크/서버 에러에 한해 최대 3회)
 * - onAuthStateChange에서 fetchProfile 실패 시 기존 profile 유지
 *   (실제 로그아웃 — session=null — 일 때만 profile 초기화)
 */

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { Session, User } from "@supabase/supabase-js";
import * as Sentry from "@sentry/react";
import { supabase } from "../lib/supabase";
import { authJson } from "../api/_client";
import {
  canAccess,
  canWrite as canWriteLevel,
  type PageGroupKey,
  type PermLevelCode,
} from "../../shared/rbac";
import type { Edition } from "../../shared/external-edition";

/** /api/v2/auth/me 응답 타입 */
// lead = 데모신청 리드 본인 계정(매장 무소속·전 권한 none). 평소엔 관리자 콘솔
// (admin.phoneboard.kr)만 쓰지만, 영업 시연 때 이 메인 앱에 직접 로그인시켜
// "전체 추출 리베표" 한 화면만 보여준다 — src/lib/leadDemo.ts 가 그 범위의 SSOT.
// ⚠️ 2026-08-28: superadmin 을 없앴다(진본 shared/rbac.ts ROLE_CODES).
export type Role = "admin" | "owner" | "tenant" | "lead";

export interface UserProfile {
  id: string;
  displayName: string | null;
  role: Role;
  tenantId: string | null;
  tenantName: string | null;
  // 본사(전역) 관리자가 임시 보기로 들어가 있는 매장. 없으면 null(본진 화면).
  // 본진 신분(tenantId/role)은 그대로라 isGlobalAdmin 판정엔 영향이 없다.
  viewingTenantId?: string | null;
  viewingTenantName?: string | null;
  // task 107: 매장 마진 (L4 표시 레이어). admin/superadmin은 null.
  marginDefault: number | null;
  // 가격비교 도메인(정책관리 복원)이 참조. 현재 백엔드 auth/me는 미제공 → 옵셔널.
  retailerId?: string | null;
  // RBAC: 이 사용자에게 적용되는 페이지그룹별 권한(none/read/write).
  // 백엔드 auth/me가 내려준다. 아직 안 내려오는 동안엔 undefined → 막지 않고 통과(규칙2).
  permissions?: Record<PageGroupKey, PermLevelCode>;
  // 상위 판매점(부모 매장) 판정 — 내 매장 밑 직속 자식이 1개 이상이고 내가 그 매장 owner일 때 true.
  // 백엔드 auth/me가 내려준다. 부모 편의 UI("하위 판매점 관리") 노출 기준(프론트는 보안 경계 아님).
  isParentStore?: boolean;
  // 내가 자식 매장이면 나를 만든 부모 매장의 tenantId. 부모/일반 계정은 null.
  parentTenantId?: string | null;
  // null/undefined = 일반·부모 매장(기존 메뉴 규칙), 배열 = 도도매 업체에 실제로 열린 LNB 경로.
  // 빈 배열은 기능 배포 직후/전부 끈 상태다. 그 업체 직원도 같은 배열을 받는다.
  childStoreMenuPaths?: string[] | null;
  // 계정이 만들어진 시각(ISO). 예전엔 화면이 이 값으로 얼굴을 판정했지만, 지금은 서버가
  // 판정해 아래 edition 으로 내려준다(폴백으로만 쓰인다). 화면·목록 등 다른 용도로 남긴다.
  createdAt?: string | null;
  // 이 계정이 보는 얼굴 — **서버 판정이 진본**이다(shared/external-edition.ts resolveEdition).
  // 화면이 매장 칸을 알 방법이 없으므로 계산하지 않고 받아 쓴다. 옛 서버는 안 내려주므로
  // 옵셔널이고, 없으면 내부용으로 친다(쓰던 매장의 화면이 사라지는 게 제일 나쁜 실패다).
  edition?: Edition;
  // 외부용일 때 **지금 켜져 있는** 기능 key 들. 관리자가 /admin/external-features 에서 정한다.
  // 내부용 계정에는 빈 배열이 온다(쓰지 않는다).
  //
  // 🕳️ 이 값이 프로필과 **같은 응답**에 실려 오는 게 중요하다. 따로 받으면 도착하기 전 찰나에
  //    "열린 게 아무것도 없음"으로 보여, 그때 가드가 판정하면 외부용 사용자가 자기가 쓸 수 있는
  //    화면에서도 튕겨 나간다(EditionContext 의 ready 함정과 같은 모양).
  externalFeatures?: string[];
}

interface AuthState {
  session: Session | null;
  user: User | null;
  profile: UserProfile | null;
  /** true이면 auth + profile 초기 로드 완료 */
  loading: boolean;
  /** true이면 profile fetch가 진행 중 (session 있지만 profile 아직 없음) */
  profileLoading: boolean;
  signOut: () => Promise<void>;
  isAdmin: () => boolean;
  /**
   * 전역 관리자 판정 — 시스템 전체를 운영하는 관리자.
   * superadmin 이거나, tenant 소속이 없는(tenantId == null) admin.
   * 매장 소속 admin("매장대표")은 false → 시스템 기능(매장/크롤링/카탈로그) 차단 기준.
   */
  isGlobalAdmin: () => boolean;
  /**
   * 매장대표 판정 — 매장 단위 기능(직원 관리·데이터 가져오기 등) 노출 기준.
   * 매장 소속 owner(또는 디버그용 매장 소속 superadmin)이면 true.
   * 본사 admin 은 false(본사는 전 매장 관리 /admin/* 를 쓴다). 매장직원(tenant)도 false.
   */
  isStoreManager: () => boolean;
  /**
   * 리드 시연 계정인가 — 영업 시연을 위해 메인 앱에 로그인한 예비 고객.
   * true이면 LNB·라우트가 시연 화면 하나로 좁혀진다(src/lib/leadDemo.ts).
   */
  isLead: () => boolean;
  /**
   * 본사 관리자가 특정 매장을 "임시 보기"로 들여다보는 중인가.
   * true이면 헤더에 "○○ 매장 보는 중 · 본사로 나가기" 배너를 띄운다.
   */
  isViewingTenant: () => boolean;
  /**
   * 상위 판매점 판정 — 자기 밑에 직속 자식 매장을 둔 부모 매장 owner인가.
   * true이면 매장 관리 메뉴에 "하위 판매점 관리"를 노출한다(프론트 편의 UI, 서버가 강제).
   */
  isParentStore: () => boolean;
  /**
   * RBAC: 이 페이지그룹에 "접근 가능"(읽기 이상)한가 — 메뉴 노출·라우트 진입 기준.
   * permissions가 아직 없으면(로딩 중 또는 백엔드 미제공) true 반환 — 막지 않는다(규칙2).
   */
  can: (group: PageGroupKey) => boolean;
  /**
   * RBAC: 이 페이지그룹에 "쓰기 가능"(생성·수정·삭제)한가 — 쓰기 버튼 기준.
   * permissions가 아직 없으면 true 반환(규칙2).
   */
  canWrite: (group: PageGroupKey) => boolean;
  /**
   * **본사 업무를 위임받았나** — 본사 전용 화면(영업 리드·수신 메시지·판매점 리드)과
   * 선택 이용 기능 스위치의 노출·진입 판정.
   *
   * 전역 관리자 **또는** `hq_shared`(본사 공용 데이터) 쓰기 권한을 받은 사람.
   * 백엔드 `hasHqDelegation` 과 같은 판정이다 — 화면과 API 가 갈리면
   * "메뉴는 보이는데 열면 튕김"이 난다.
   *
   * ⚠️ 2026-08-28 이전에는 라인업 매장 번호(`admin-demo`)를 코드에 박아 그 한 매장만
   *    열어 줬다. 이제 본사가 화면(`/admin/store-members`)에서 켜고 끈다.
   */
  hasHqDelegation: () => boolean;
  /** task 107: 마진 저장 후 profile.marginDefault를 즉시 갱신한다 */
  updateMargin: (marginDefault: number) => void;
  /** 외부용 기능 스위치 저장 후 미리보기 목록을 즉시 갱신한다. */
  updateExternalFeature: (key: string, enabled: boolean) => void;
}

const AuthContext = createContext<AuthState>({
  session: null,
  user: null,
  profile: null,
  loading: true,
  profileLoading: false,
  signOut: async () => {},
  isAdmin: () => false,
  isGlobalAdmin: () => false,
  isStoreManager: () => false,
  isLead: () => false,
  isViewingTenant: () => false,
  isParentStore: () => false,
  can: () => true,
  canWrite: () => true,
  hasHqDelegation: () => false,
  updateMargin: () => {},
  updateExternalFeature: () => {},
});

/**
 * /api/v2/auth/me 조회.
 * 네트워크/서버 에러(5xx) 시 최대 MAX_RETRIES번 재시도.
 * 401/403/404 등 auth 에러는 즉시 null 반환 (재시도 불필요).
 *
 * 토큰은 authJson(공통 클라이언트)이 매 호출 getSession()으로 직접 얻는다 — 호출부가 캡처한
 * access_token 을 넘기지 않는다. 핵심은 authJson 이 401 을 만나면 **세션을 한 번 갱신하고
 * 새 토큰으로 1회 재시도**한다는 것. 토큰 만료 경계의 찰나에 옛 토큰으로 /me 가 나가도
 * 여기서 갱신·복구되어 "프로필 없음=로그아웃"으로 오인되지 않는다. (재시도 후에도 401 이면
 * 진짜 만료 → 아래 catch 가 null 로 떨궈 정상 로그아웃 흐름을 탄다.)
 */
async function fetchProfile(): Promise<UserProfile | null> {
  const MAX_RETRIES = 3;
  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      return await authJson<UserProfile>(
        `${import.meta.env.VITE_API_BASE_URL ?? ""}/api/v2/auth/me`,
      );
    } catch (e) {
      // authJson 은 실패 시 ApiError(.status 보유)를 throw 한다.
      // auth/not-found(401 갱신재시도 후에도 실패 · 403 · 404)는 재시도 무의미 → null.
      const status = (e as { status?: number })?.status;
      if (status === 401 || status === 403 || status === 404) return null;
      // 서버 에러(5xx)·네트워크 에러: 재시도
    }
    if (attempt < MAX_RETRIES - 1) {
      await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
    }
  }
  return null;
}

/**
 * 로그인한 사용자를 관측 도구(Sentry + Datadog RUM)에 묶는다 —
 * 에러가 떴을 때(Sentry), 화면이 느렸을 때(Datadog 세션 재생)
 * "누가(어느 매장) 겪었는지"를 양쪽에 똑같이 남긴다.
 *
 * id/email은 세션에서, role/매장 정보는 profile에서 가져온다(profile은 아직 로딩 전일 수 있어 옵셔널).
 * 세션이 없으면(로그아웃) 양쪽 다 비운다.
 *
 * Sentry와 Datadog의 사용자 식별을 한 곳에서 함께 처리해, 둘이 어긋날 일이 없게 한다.
 */
function syncObservabilityUser(
  session: Session | null,
  profile: UserProfile | null,
) {
  if (session?.user) {
    const id = session.user.id;
    const role = profile?.role;
    // 매장명 우선, 없으면 매장ID — 전역 관리자는 둘 다 비어 "본사"로 식별된다
    const tenant = profile?.tenantName ?? profile?.tenantId ?? undefined;

    // 🔒 이메일은 싣지 않는다 — 내부 번호(id)만(공통 기록 규격 §3, 2026-10-06). 사람은 id 로 DB 에서 찾는다.
    Sentry.setUser({ id, role, tenant });
  } else {
    Sentry.setUser(null);
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);

  // onAuthStateChange 중복 호출 방지용 (INITIAL_SESSION + getSession 경쟁)
  const initialLoadDone = useRef(false);
  // 같은 브라우저에서 로그아웃 없이 다른 계정으로 세션이 교체될 수도 있다. 새 세션의
  // /auth/me가 끝나기 전까지 옛 profile을 유지하면 메뉴·권한·tenantId가 앞 계정 것으로
  // 렌더되므로, 세션 사용자 경계에서 즉시 비우고 늦게 끝난 옛 요청도 무시한다.
  const authUserIdRef = useRef<string | null>(null);
  const profileRequestGenerationRef = useRef(0);

  useEffect(() => {
    // 초기 세션 로드
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      const userId = session?.user.id ?? null;
      authUserIdRef.current = userId;
      const requestGeneration = ++profileRequestGenerationRef.current;
      setSession(session);
      // getSession 자체는 끝났으므로 auth 초기 로드는 여기서 닫는다. profile은 별도
      // profileLoading으로 보호한다. 이 값을 profile 응답 뒤까지 미루면, 그 사이 다른 계정으로
      // 바뀌어 앞 요청을 폐기했을 때 onAuthStateChange도 "초기 로드 중"으로 오인해 새 profile
      // 조회를 건너뛰고 앱이 영구 로딩에 빠질 수 있다.
      setLoading(false);
      initialLoadDone.current = true;
      if (session?.access_token) {
        setProfileLoading(true);
        const p = await fetchProfile();
        if (
          requestGeneration !== profileRequestGenerationRef.current ||
          authUserIdRef.current !== userId
        ) {
          return;
        }
        setProfile(p);
        syncObservabilityUser(session, p);
        setProfileLoading(false);
      } else {
        syncObservabilityUser(null, null);
      }
    });

    // 인증 상태 변경 리스너
    //
    // ⚠️ Supabase 데드락 함정: onAuthStateChange 콜백은 supabase-js 내부 auth 락(navigator
    // LockManager)을 쥔 채 실행된다. 이 콜백 안에서 다시 supabase.auth.getSession()/
    // refreshSession() 등 같은 락이 필요한 호출을 await 하면, 콜백은 그 호출이 끝나길·그 호출은
    // 콜백이 락을 놓길 서로 기다리며 영원히 멈춘다. fetchProfile → authJson → buildAuthHeaders 가
    // getSession() 을 부르므로, 콜백 안에서 직접 await 하면 SIGNED_IN 직후(로그인 성공 순간)
    // /auth/me 가 영영 안 나가고 화면이 로그인 페이지에 갇힌다(새로고침=getSession().then() 경로는
    // 콜백 밖이라 멀쩡 → "가끔 되고 가끔 안 되는" 불안정으로 보였다). 그래서 profile 조회를
    // setTimeout(…, 0) 으로 한 틱 미뤄, 콜백이 먼저 리턴해 락을 푼 뒤 실행되게 한다.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      const nextUserId = session?.user.id ?? null;
      if (authUserIdRef.current !== nextUserId) {
        // 계정 A의 profile을 계정 B가 단 한 프레임도 쓰지 않게 한다. profileLoading 동안
        // 권한 가드들은 스피너를 유지하고, 캐시 경계도 이 사용자 변경을 보고 즉시 비운다.
        profileRequestGenerationRef.current += 1;
        setProfile(null);
      }
      authUserIdRef.current = nextUserId;
      setSession(session);

      if (session?.access_token) {
        // 초기 로드 전(INITIAL_SESSION)이면 getSession().then()에 위임
        if (!initialLoadDone.current) return;

        // TOKEN_REFRESHED, SIGNED_IN 등: profile 재조회.
        // 락 해제 후 실행되도록 콜백 밖(다음 매크로태스크)으로 미룬다 — 위 데드락 주석 참고.
        setProfileLoading(true);
        const requestGeneration = ++profileRequestGenerationRef.current;
        setTimeout(async () => {
          const p = await fetchProfile();
          if (
            requestGeneration !== profileRequestGenerationRef.current ||
            authUserIdRef.current !== nextUserId
          ) {
            return;
          }
          if (p !== null) {
            // 성공한 경우만 profile 업데이트
            setProfile(p);
          }
          // null(fetchProfile 실패)이면 기존 profile 유지 — 서버 재시작 등 일시적 장애 대응.
          // profile이 없어도 세션의 id/email은 항상 Sentry에 묶는다.
          syncObservabilityUser(session, p);
          setProfileLoading(false);
        }, 0);
      } else {
        // session=null: 실제 로그아웃 — profile 초기화
        profileRequestGenerationRef.current += 1;
        setProfile(null);
        syncObservabilityUser(null, null);
        setProfileLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setProfile(null);
    syncObservabilityUser(null, null);
  };

  // 관리자 메뉴/페이지 노출 기준.
  const isAdmin = () => profile?.role === "admin";

  // 전역(본사) 관리자 — 매장 소속이 없는 admin(tenantId == null).
  // 매장 소속 admin("매장대표")은 false → 시스템 기능 차단.
  const isGlobalAdmin = () =>
    profile?.role === "admin" && profile?.tenantId == null;

  // 매장대표 — 자기 매장에 소속된 owner.
  // 매장 단위 기능(직원 관리·데이터 가져오기) 메뉴/페이지 노출 기준. 백엔드 requireStoreManager
  // 와 같은 기준. 본사 admin 은 해당 없음(전 매장 관리 /admin/* 사용), 매장직원도 제외.
  const isStoreManager = () =>
    profile?.role === "owner" && profile?.tenantId != null;

  // 리드 시연 계정 — 예비 고객이 영업 시연용으로 로그인한 상태.
  // 권한 매트릭스상 모든 그룹이 none 이라 평소 규칙대로면 갈 곳이 없다. 이 플래그를 본 곳만
  // 시연 화면 하나를 열어 준다(권한을 넓히는 게 아니라 예외 한 칸을 내는 방식).
  const isLead = () => profile?.role === "lead";

  // 임시 보기 중 — 본사 관리자가 특정 매장을 들여다보는 상태.
  const isViewingTenant = () => Boolean(profile?.viewingTenantId);

  // 상위 판매점 — 백엔드 auth/me가 내려준 isParentStore 를 그대로 통과.
  // 직속 자식 매장을 둔 부모 owner 만 true → "하위 판매점 관리" 메뉴 노출 기준.
  const isParentStore = () => profile?.isParentStore === true;

  // RBAC 접근 판정 — permissions가 아직 없으면(undefined) 막지 않고 통과(규칙2).
  // 명시적으로 받은 값이 none일 때만 차단, read 이상이면 접근 허용.
  const can = (group: PageGroupKey) => {
    const level = profile?.permissions?.[group];
    if (level === undefined) return true;
    return canAccess(level);
  };
  // RBAC 쓰기 판정 — permissions 없으면 통과. read이면 쓰기 불가, write만 허용.
  const canWrite = (group: PageGroupKey) => {
    const level = profile?.permissions?.[group];
    if (level === undefined) return true;
    return canWriteLevel(level);
  };

  // 본사 위임 판정 — 전역 관리자거나 hq_shared 쓰기를 받은 사람. 백엔드와 같은 선.
  // ⚠️ canWrite 는 권한이 아직 안 도착했을 때 true 를 주므로(규칙2), 프로필이 없으면
  //    여기서 먼저 false 로 끊는다 — 로그인 직후 한 순간 본사 메뉴가 번쩍이지 않게.
  const hasHqDelegation = () => profile != null && (isGlobalAdmin() || canWrite("hq_shared"));

  // task 107: 마진 저장 후 전체 profile fetch 없이 marginDefault만 즉시 반영
  const updateMargin = (marginDefault: number) => {
    setProfile((prev) => (prev ? { ...prev, marginDefault } : prev));
  };

  // 기능 스위치를 누른 뒤 /auth/me 전체를 다시 받지 않고 미리보기 목록만 맞춘다.
  // 안 그러면 방금 기능을 열어도 헤더의 [외부용] 탭이 저장 전 빈 목록을 계속 본다.
  const updateExternalFeature = (key: string, enabled: boolean) => {
    setProfile((prev) => {
      if (!prev) return prev;
      const current = prev.externalFeatures ?? [];
      const externalFeatures = enabled
        ? current.includes(key)
          ? current
          : [...current, key]
        : current.filter((item) => item !== key);
      return { ...prev, externalFeatures };
    });
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        user: session?.user ?? null,
        profile,
        loading,
        profileLoading,
        signOut,
        isAdmin,
        isGlobalAdmin,
        isStoreManager,
        isLead,
        isViewingTenant,
        isParentStore,
        can,
        canWrite,
        hasHqDelegation,
        updateMargin,
        updateExternalFeature,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
